import React from "react";
import { Container, Box, Typography, Tabs, Tab } from "@mui/material";
import Footer from "../components/Footer";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`legal-tabpanel-${index}`}
      aria-labelledby={`legal-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const RoutinexLegal: React.FC = () => {
  const [value, setValue] = React.useState(0);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  return (
    <Box sx={{ minHeight: "100vh", display: "flex", flexDirection: "column" }}>
      <Container maxWidth="md" sx={{ py: 4, flex: 1 }}>
        <Typography
          variant="h3"
          sx={{ fontWeight: 700, mb: 3, textAlign: "center" }}
        >
          RoutineX Legal
        </Typography>

        <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
          <Tabs value={value} onChange={handleChange} centered>
            <Tab label="Terms & Conditions" />
            <Tab label="Privacy Policy" />
          </Tabs>
        </Box>

        <TabPanel value={value} index={0}>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
            Terms and Conditions
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
            Last updated: November 16, 2025
          </Typography>

          <Box
            sx={{
              "& h5": { fontSize: "1.25rem", fontWeight: 600, mt: 3, mb: 1.5 },
              "& p, & ul": { lineHeight: 1.8, mb: 2 },
            }}
          >
            <Typography variant="h5">1. Acceptance of Terms</Typography>
            <Typography variant="body1">
              By downloading, installing, or using RoutineX ("the App"), you
              agree to be bound by these Terms and Conditions. If you do not
              agree to these terms, please do not use the App.
            </Typography>

            <Typography variant="h5">2. Description of Service</Typography>
            <Typography variant="body1">
              RoutineX is a habit tracking and challenge management application
              that helps users build and maintain healthy habits. The App
              includes features such as:
            </Typography>
            <Box component="ul" sx={{ pl: 3 }}>
              <li>Daily habit tracking</li>
              <li>Challenge creation and management</li>
              <li>Progress tracking and statistics</li>
              <li>AI-powered chat assistance (on-device)</li>
              <li>Offline functionality</li>
              <li>Ad-supported features</li>
            </Box>

            <Typography variant="h5">3. User Responsibilities</Typography>
            <Typography variant="body1">You agree to:</Typography>
            <Box component="ul" sx={{ pl: 3 }}>
              <li>Provide accurate information when using the App</li>
              <li>Use the App only for lawful purposes</li>
              <li>
                Not attempt to reverse engineer, decompile, or disassemble the
                App
              </li>
              <li>
                Not use the App in any way that could damage, disable, or impair
                the App
              </li>
              <li>Comply with all applicable laws and regulations</li>
            </Box>

            <Typography variant="h5">4. Intellectual Property</Typography>
            <Typography variant="body1">
              All content, features, and functionality of the App, including but
              not limited to text, graphics, logos, icons, images, and software,
              are the exclusive property of RoutineX and are protected by
              international copyright, trademark, and other intellectual
              property laws.
            </Typography>

            <Typography variant="h5">5. Data Storage and Privacy</Typography>
            <Box component="ul" sx={{ pl: 3 }}>
              <li>
                All user data is stored locally on your device using SQLite
              </li>
              <li>
                The App works 100% offline and does not sync data to cloud
                servers
              </li>
              <li>No user accounts or authentication are required</li>
              <li>We do not collect, store, or share your personal data</li>
              <li>Please refer to our Privacy Policy for more details</li>
            </Box>

            <Typography variant="h5">6. Advertising</Typography>
            <Typography variant="body1">
              The App is ad-supported and displays advertisements through Google
              AdMob. By using the App, you agree to view these advertisements.
              Some premium features may require watching reward ads to unlock.
            </Typography>

            <Typography variant="h5">7. AI Features</Typography>
            <Typography variant="body1">
              The App includes on-device AI capabilities powered by local model. All
              AI processing occurs locally on your device, and no data is sent
              to external servers. The AI assistance is provided "as is" without
              warranties of accuracy or completeness.
            </Typography>

            <Typography variant="h5">8. Disclaimers</Typography>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              THE APP IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES
              OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED
              TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR
              PURPOSE, OR NON-INFRINGEMENT.
            </Typography>
            <Typography variant="body1">We do not warrant that:</Typography>
            <Box component="ul" sx={{ pl: 3 }}>
              <li>The App will be error-free or uninterrupted</li>
              <li>Defects will be corrected</li>
              <li>The App is free of viruses or other harmful components</li>
              <li>Results from using the App will be accurate or reliable</li>
            </Box>

            <Typography variant="h5">9. Limitation of Liability</Typography>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, WE SHALL NOT BE LIABLE FOR
              ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE
              DAMAGES, OR ANY LOSS OF PROFITS OR REVENUES, WHETHER INCURRED
              DIRECTLY OR INDIRECTLY, OR ANY LOSS OF DATA, USE, GOODWILL, OR
              OTHER INTANGIBLE LOSSES.
            </Typography>

            <Typography variant="h5">10. Changes to Terms</Typography>
            <Typography variant="body1">
              We reserve the right to modify these Terms and Conditions at any
              time. Changes will be effective immediately upon posting within
              the App. Your continued use of the App after changes constitutes
              acceptance of the modified terms.
            </Typography>

            <Typography variant="h5">11. Termination</Typography>
            <Typography variant="body1">
              We may terminate or suspend your access to the App immediately,
              without prior notice, for any reason, including if you breach
              these Terms and Conditions.
            </Typography>

            <Typography variant="h5">12. Governing Law</Typography>
            <Typography variant="body1">
              These Terms and Conditions shall be governed by and construed in
              accordance with the laws of your jurisdiction, without regard to
              its conflict of law provisions.
            </Typography>

            <Typography variant="h5">13. Contact Information</Typography>
            <Typography variant="body1">
              If you have any questions about these Terms and Conditions, please
              contact us through the App or visit our website.
            </Typography>
          </Box>
        </TabPanel>

        <TabPanel value={value} index={1}>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
            Privacy Policy
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
            Last updated: November 16, 2025
          </Typography>

          <Box
            sx={{
              "& h5": { fontSize: "1.25rem", fontWeight: 600, mt: 3, mb: 1.5 },
              "& p, & ul": { lineHeight: 1.8, mb: 2 },
            }}
          >
            <Typography variant="h5">Introduction</Typography>
            <Typography variant="body1">
              RoutineX ("we", "our", or "the App") is committed to protecting
              your privacy. This Privacy Policy explains how we handle your
              information when you use our habit tracking application.
            </Typography>

            <Typography variant="h5">Our Privacy-First Approach</Typography>
            <Typography variant="body1">
              RoutineX is designed with privacy in mind and operates 100%
              offline. We do NOT collect, store, or transmit any personal
              information to our servers.
            </Typography>

            <Typography variant="h5">Data Collection</Typography>
            <Typography variant="body1">
              <strong>We Do Not Collect Personal Data</strong>
            </Typography>
            <Typography variant="body1">
              RoutineX does not require user registration, login, or any form of
              authentication. We do not collect:
            </Typography>
            <Box component="ul" sx={{ pl: 3 }}>
              <li>Names, email addresses, or contact information</li>
              <li>Location data or GPS coordinates</li>
              <li>Device identifiers or unique user IDs</li>
              <li>Browsing history or app usage patterns</li>
              <li>Personal photos or media files</li>
            </Box>

            <Typography variant="h5">Local Data Storage</Typography>
            <Typography variant="body1">
              All your data is stored locally on your device using SQLite
              database:
            </Typography>
            <Box component="ul" sx={{ pl: 3 }}>
              <li>Habits and challenges you create</li>
              <li>Progress tracking and statistics</li>
              <li>App settings and preferences</li>
              <li>AI chat conversations (processed on-device only)</li>
            </Box>
            <Typography variant="body1">
              This data remains on your device and is never transmitted to our
              servers or any third parties.
            </Typography>

            <Typography variant="h5">Third-Party Services</Typography>
            <Typography variant="body1">
              <strong>Google AdMob</strong>
            </Typography>
            <Typography variant="body1">
              RoutineX is ad-supported through Google AdMob. When ads are
              displayed:
            </Typography>
            <Box component="ul" sx={{ pl: 3 }}>
              <li>Google may collect advertising identifiers and usage data</li>
              <li>Ad networks may use cookies and tracking technologies</li>
              <li>
                You can opt out of personalized ads through your device settings
              </li>
            </Box>
            <Typography variant="body1">
              For more information, please review Google's Privacy Policy at
              https://policies.google.com/privacy
            </Typography>

            <Typography variant="h5">AI Features</Typography>
            <Typography variant="body1">
              The App includes on-device AI capabilities powered by local model:
            </Typography>
            <Box component="ul" sx={{ pl: 3 }}>
              <li>All AI processing occurs locally on your device</li>
              <li>Chat conversations are not sent to external servers</li>
              <li>
                AI responses are generated on-device without internet connection
              </li>
              <li>
                Your conversations remain private and are stored only on your
                device
              </li>
            </Box>

            <Typography variant="h5">Children's Privacy</Typography>
            <Typography variant="body1">
              RoutineX does not knowingly collect or solicit information from
              anyone under the age of 13. Since we do not collect any personal
              information, the App can be used by users of all ages with
              parental supervision as appropriate.
            </Typography>

            <Typography variant="h5">Data Security</Typography>
            <Typography variant="body1">
              Since all data is stored locally on your device:
            </Typography>
            <Box component="ul" sx={{ pl: 3 }}>
              <li>
                Your data security depends on your device's security measures
              </li>
              <li>
                We recommend using device encryption and screen lock features
              </li>
              <li>Backing up your device will include RoutineX data</li>
              <li>Uninstalling the App will delete all local data</li>
            </Box>

            <Typography variant="h5">Data Retention and Deletion</Typography>
            <Typography variant="body1">
              You have complete control over your data:
            </Typography>
            <Box component="ul" sx={{ pl: 3 }}>
              <li>Delete individual habits and challenges within the App</li>
              <li>Clear all data through the App's settings</li>
              <li>Uninstall the App to completely remove all data</li>
            </Box>

            <Typography variant="h5">International Data Transfers</Typography>
            <Typography variant="body1">
              RoutineX operates entirely offline on your device. Since no data
              is transmitted to servers, there are no cross-border data
              transfers or international data processing concerns.
            </Typography>

            <Typography variant="h5">Changes to This Privacy Policy</Typography>
            <Typography variant="body1">
              We may update this Privacy Policy from time to time. Changes will
              be posted within the App, and the "Last updated" date will be
              revised. Your continued use of the App after changes constitutes
              acceptance of the updated Privacy Policy.
            </Typography>

            <Typography variant="h5">Contact Us</Typography>
            <Typography variant="body1">
              If you have any questions about this Privacy Policy or our privacy
              practices, please contact us at: sharmashashi940@gmail.com
            </Typography>

            <Box
              sx={{
                mt: 4,
                p: 2,
                backgroundColor: "rgba(145, 189, 58, 0.1)",
                borderRadius: 2,
                borderLeft: "4px solid",
                borderColor: "primary.main",
              }}
            >
              <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                Privacy Summary
              </Typography>
              <Typography variant="body2">
                RoutineX is built with privacy as a core principle. Your data
                never leaves your device, there are no user accounts, and all AI
                processing happens locally. You maintain complete control and
                ownership of your information.
              </Typography>
            </Box>
          </Box>
        </TabPanel>
      </Container>
      <Footer />
    </Box>
  );
};

export default RoutinexLegal;
